/**
 * Site-scoping helpers.
 *
 * Centralises the "filter by the active site" predicate so the policy
 * lives in one place. The default predicate treats entities without a
 * `siteId` as belonging to every site — backward compatibility for
 * upstream APIs (real RMF, future ingest sources) that haven't yet
 * tagged every entity. Switch to the strict variant (via the
 * `siteFilterStrict` feature flag) once the upstream API tags every
 * entity, so untagged data is hidden.
 *
 * Cache-key constants for the site-scoped TanStack Query entries.
 * Use these everywhere `['robots' | 'tasks' | 'alerts', siteId]`
 * keys are constructed so a future rename can land in a single file.
 */
import { getRuntimeConfig } from '../config/runtime-config';

export const ALERTS_KEY = 'alerts' as const;
export const DOORS_KEY = 'doors' as const;
export const LIFTS_KEY = 'lifts' as const;
export const BUILDING_MAP_KEY = 'building_map' as const;
export const FIRE_ALARM_KEY = 'fire_alarm' as const;
export const TRAJECTORIES_KEY = 'trajectories' as const;
/** Per-site trajectory-feed health (publisher `stale` flag), by map — see `useTrajectoryFeedStale`. */
export const TRAJECTORY_FEED_KEY = 'trajectory_feed' as const;
export const METRICS_KEY = 'metrics' as const;
export const ISSUES_KEY = 'issues' as const;
export const FAVORITES_KEY = 'favorites' as const;
export const TICKETS_KEY = 'tickets' as const;
/** Server-side `count(*)` badge feeds (`GET .../alerts/summary`,
 *  `GET .../issues/summary`) — separate keys from the row lists so a
 *  badge invalidation never forces a full-list refetch and vice versa. */
export const ALERT_SUMMARY_KEY = 'alert_summary' as const;
export const ISSUE_SUMMARY_KEY = 'issue_summary' as const;
/** Per-device alert mutes (`GET .../alerts/mutes`). */
export const ALERT_MUTES_KEY = 'alert_mutes' as const;

// `robotsKey`/`tasksKey` (`['robots'|'tasks', siteId]`) were deleted
// 2026-09-02: NO query was ever registered under either -- the robot
// and task lists read `robotsQueryOptions`/`tasksQueryOptions`' v1 keys
// -- so every invalidation aimed at them was a silent no-op, and four
// hooks were aiming at them. The wrong key being unreachable is the
// durable half of that fix.
export const alertsKey = (siteId: string) => [ALERTS_KEY, siteId] as const;
export const doorsKey = (siteId: string) => [DOORS_KEY, siteId] as const;
export const liftsKey = (siteId: string) => [LIFTS_KEY, siteId] as const;
/**
 * TWO SHAPES, TWO KEYS (twin slice 1, plan §2.1 / X28). `buildingMapKey`
 * holds the ASSEMBLED legacy `BuildingMap` the 2D canvas renders
 * (`useBuildingMap`); `buildingMapSnapshotKey` holds the RAW v1
 * `BuildingMapSnapshot` the twin's floorplans decode (`useFloorplans`).
 * They used to share `buildingMapKey`, so whichever view loaded first set
 * the shape and the other then read the wrong one: Map then Building drew
 * no floorplan, and Building then Map read `levels` off a snapshot. The
 * assembled query takes its raw part from the snapshot key
 * (`queryClient.fetchQuery`), so one network fetch still serves both.
 *
 * Four segments with a literal `'snapshot'`, so it never equals a
 * three-segment assembled key whatever a level is called, and still under
 * the `[BUILDING_MAP_KEY, siteId]` prefix the SSE `building_map_revision`
 * notification invalidates.
 */
export const buildingMapKey = (siteId: string, level: string) => [BUILDING_MAP_KEY, siteId, level] as const;
export const buildingMapSnapshotKey = (siteId: string, level: string) =>
  [BUILDING_MAP_KEY, siteId, 'snapshot', level] as const;
export const buildingMapLevelsKey = (siteId: string) => [BUILDING_MAP_KEY, siteId, 'levels'] as const;
export const fireAlarmKey = (siteId: string) => [FIRE_ALARM_KEY, siteId] as const;
export const trajectoriesKey = (siteId: string, map: string) => [TRAJECTORIES_KEY, siteId, map] as const;
export const trajectoryFeedKey = (siteId: string) => [TRAJECTORY_FEED_KEY, siteId] as const;
/** A fixed site-local calendar range for `GET .../metrics`, both ends
 *  `YYYY-MM-DD`, `to` inclusive. */
export interface MetricsRange {
  from: string;
  to: string;
}
/**
 * Rolling window: `[metrics, site, days, granularity]`, unchanged.
 * Custom range: `[metrics, site, 'range', from, to, granularity]` —
 * the hook does not send `days` with a range, so the key must not
 * carry it either (two quick-pick states with the same range would
 * otherwise be two cache slots of identical data). Both shapes keep
 * the `[METRICS_KEY, siteId]` prefix `useDeleteTaskHistory` invalidates
 * by, so a deleted task falls out of every variant.
 */
export const metricsKey = (
  siteId: string,
  days: number,
  granularity: 'hour' | 'day' = 'day',
  range?: MetricsRange | null,
) =>
  range
    ? ([METRICS_KEY, siteId, 'range', range.from, range.to, granularity] as const)
    : ([METRICS_KEY, siteId, days, granularity] as const);
export const issuesKey = (siteId: string, source?: string) => [ISSUES_KEY, siteId, source ?? 'all'] as const;
export const favoritesKey = (siteId: string) => [FAVORITES_KEY, siteId] as const;
export const ticketsKey = (siteId: string) => [TICKETS_KEY, siteId] as const;
/**
 * Prefixed with `ticketsKey`'s own root on purpose: the existing
 * `invalidateQueries({ queryKey: ticketsKey(siteId) })` after filing a
 * ticket then also refreshes any open detail, because react-query
 * matches key prefixes.
 */
export const ticketDetailKey = (siteId: string, ticketId: string) =>
  [TICKETS_KEY, siteId, ticketId] as const;
export const alertSummaryKey = (siteId: string) => [ALERT_SUMMARY_KEY, siteId] as const;
/**
 * `sources` mirrors the CSV `?sources=` filter on the summary
 * endpoint. Sorted before joining so `['a','b']` and `['b','a']`
 * share one cache slot; `undefined`/empty means "no filter" and keys
 * on `'all'`, matching `issuesKey`'s convention. Invalidate by the
 * `[ISSUE_SUMMARY_KEY, siteId]` prefix so every source-set variant
 * refetches together.
 */
export const issueSummaryKey = (siteId: string, sources?: readonly string[]) =>
  [ISSUE_SUMMARY_KEY, siteId, sources && sources.length > 0 ? [...sources].sort().join(',') : 'all'] as const;
export const alertMutesKey = (siteId: string) => [ALERT_MUTES_KEY, siteId] as const;

export interface SiteScoped {
  siteId?: string;
}

/**
 * Filter `items` to those belonging to the active site. When the
 * `siteFilterStrict` feature flag is true, entities without a
 * `siteId` are dropped; otherwise they are kept (treated as
 * global) for backward compatibility with APIs that don't yet tag
 * every entity.
 */
export function scopeByActiveSite<T extends SiteScoped>(items: T[], siteId: string): T[] {
  const strict = getRuntimeConfig().featureFlags.siteFilterStrict;
  if (strict) {
    return items.filter((it) => it.siteId === siteId);
  }
  return items.filter((it) => !it.siteId || it.siteId === siteId);
}

/**
 * Strict variant: drops entities without a `siteId`. Use when the
 * upstream API is known to tag every entity (e.g. the mock server).
 */
export function scopeByActiveSiteStrict<T extends SiteScoped>(items: T[], siteId: string): T[] {
  return items.filter((it) => it.siteId === siteId);
}
